import { FC, memo, useMemo, useCallback, useEffect, useRef } from "react";
import {
  DataGridPremium,
  GridRowModelUpdate,
  GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import {
  columnGroupingModel,
  CLUSTER_START_CLASS,
  columns,
  PRODUCT_FIELD,
} from "./columns";
import { blueGrey, grey, brown } from "@mui/material/colors";
import {
  askField,
  bidField,
  clusterIdOfField,
  selectedCellKey,
  spotField,
  useAllGridsOrderbook,
  useSelectedProducts,
  WideOrderBookRow,
} from "./hooks";
import { AREA_CLUSTERS } from "./areaClusters";
import { alpha, SxProps, Theme } from "@mui/material";

type Props = {
  columnVisibilityModel: Record<string, boolean>;
  hideCluster: (clusterId: string) => void;
};

const ActualOrderBookGridComponent: FC<Props> = ({ columnVisibilityModel }) => {
  const apiRef = useGridApiRef();
  const previousRowsRef = useRef<Map<string, WideOrderBookRow>>(new Map());
  const rows = useAllGridsOrderbook();

  const { rowIds, cellKeys } = useSelectedProducts(rows);

  const getRowClassName = useCallback(
    (params: { row: WideOrderBookRow }) => {
      // `SingleProduct` drives the zebra striping, so it has to survive
      // alongside the suspended marker rather than be replaced by it.
      const base = rowIds.has(params.row.id) ? "highlighted" : "SingleProduct";
      return params.row.suspended ? `${base} contract-suspended` : base;
    },
    [rowIds]
  );

  const getCellClassName = useCallback(
    (params: { row: WideOrderBookRow; field: string }) => {
      const clusterId = clusterIdOfField(params.field);
      if (clusterId === undefined) {
        // Struck through rather than only dimmed: the delivery window is the one
        // cell that still carries real text on a dead row, so it is the one that
        // can say "not tradeable" without a label.
        return params.field === PRODUCT_FIELD && params.row.suspended
          ? "product-suspended"
          : "";
      }

      const classes: string[] = [];
      if (cellKeys.has(selectedCellKey(clusterId, params.row.id))) {
        classes.push("selected-cluster-cell");
      }
      // Per cluster, for the partial case: some areas of this delivery window
      // still trade while this cluster's do not, so the row itself is live.
      if (!params.row.suspended && params.row.cells[clusterId]?.suspended) {
        classes.push("cluster-suspended");
      }
      return classes.join(" ");
    },
    [cellKeys]
  );

  useEffect(() => {
    const nextRows = new Map<string, WideOrderBookRow>();
    for (const row of rows) {
      nextRows.set(row.id, row);
    }

    const updates: GridRowModelUpdate[] = [];
    for (const [id, next] of nextRows) {
      const prev = previousRowsRef.current.get(id);
      if (!prev || !isSameRow(prev, next)) {
        updates.push(next);
      }
    }

    for (const id of previousRowsRef.current.keys()) {
      if (!nextRows.has(id)) {
        updates.push({ id, _action: "delete" });
      }
    }

    const api = apiRef.current;
    if (updates.length > 0 && api) {
      api.updateRows(updates);
    }

    previousRowsRef.current = nextRows;
  }, [apiRef, rows]);

  // Keep rows prop stable and mutate DataGrid row model through apiRef.updateRows.
  const initialRows = useMemo<WideOrderBookRow[]>(() => [], []);

  return (
    <DataGridPremium
      key={"actual-orderbook-grid"}
      sx={gridStyle}
      getRowClassName={getRowClassName}
      getCellClassName={getCellClassName}
      columnGroupingModel={columnGroupingModel}
      columnVisibilityModel={columnVisibilityModel}
      initialState={{
        pinnedColumns: { left: [PRODUCT_FIELD] },
        pagination: {
          paginationModel: {
            pageSize: 10000,
          },
        },
        sorting: {
          sortModel: [
            { field: "contractDeliveryStartUtc", sort: "asc" },
            { field: "contractDeliveryEndUtc", sort: "desc" },
          ],
        },
      }}
      columns={columns}
      rows={initialRows}
      density="compact"
      rowHeight={25}
      columnHeaderHeight={32}
      columnGroupHeaderHeight={30}
      disableColumnSelector
      disableDensitySelector
      disableColumnMenu
      hideFooter
      rowSelectionModel={{
        type: "include",
        ids: new Set<GridRowId>(rowIds),
      }}
      disableMultipleRowSelection
      loading={rows.length === 0}
      apiRef={apiRef}
    />
  );
};

ActualOrderBookGridComponent.displayName = "ActualOrderBookGrid";

export const ActualOrderBookGrid = memo(ActualOrderBookGridComponent);

// Every scalar cluster field the grid renders. This list is load-bearing: the
// grid is fed a permanently-empty `rows` prop and mutated only through
// apiRef.updateRows, so a field missing here is never pushed to a row that
// already exists. Spot arrives asynchronously, long after rows are created.
const CLUSTER_VALUE_FIELDS = AREA_CLUSTERS.flatMap((cluster) => [
  bidField(cluster.id),
  askField(cluster.id),
  spotField(cluster.id),
]);

const isSameRow = (a: WideOrderBookRow, b: WideOrderBookRow): boolean => {
  if (
    a.contractDeliveryStartUtc !== b.contractDeliveryStartUtc ||
    a.contractDeliveryEndUtc !== b.contractDeliveryEndUtc ||
    a.suspended !== b.suspended
  ) {
    return false;
  }

  for (let i = 0; i < CLUSTER_VALUE_FIELDS.length; i++) {
    const field = CLUSTER_VALUE_FIELDS[i];
    if (a[field] !== b[field]) {
      return false;
    }
  }

  for (let i = 0; i < AREA_CLUSTERS.length; i++) {
    const clusterId = AREA_CLUSTERS[i].id;
    const prev = a.cells[clusterId];
    const next = b.cells[clusterId];

    if (prev === undefined || next === undefined) {
      if (prev !== next) {
        return false;
      }
      continue;
    }

    if (
      prev.suspended !== next.suspended ||
      prev.openMemberCount !== next.openMemberCount ||
      prev.topBidIsOwn !== next.topBidIsOwn ||
      prev.topAskIsOwn !== next.topAskIsOwn ||
      prev.bidMw !== next.bidMw ||
      prev.askMw !== next.askMw ||
      prev.bidDecoupled !== next.bidDecoupled ||
      prev.askDecoupled !== next.askDecoupled ||
      prev.spotDecoupled !== next.spotDecoupled ||
      prev.bidArea !== next.bidArea ||
      prev.askArea !== next.askArea ||
      prev.bidOwnArea !== next.bidOwnArea ||
      prev.askOwnArea !== next.askOwnArea ||
      prev.spotArea !== next.spotArea ||
      prev.contractName !== next.contractName
    ) {
      return false;
    }

    // The spot cell renders every member, but the flat `c<id>_spot` field and
    // `spotArea` above only describe the first one that cleared — without this,
    // a change further down the cluster leaves a stale cell on screen.
    if (prev.members.length !== next.members.length) {
      return false;
    }
    for (let m = 0; m < prev.members.length; m++) {
      const prevMember = prev.members[m];
      const nextMember = next.members[m];
      if (
        prevMember.areaCode !== nextMember.areaCode ||
        prevMember.openForTrading !== nextMember.openForTrading ||
        prevMember.spot?.yenPerMwh !== nextMember.spot?.yenPerMwh
      ) {
        return false;
      }
    }
  }

  return true;
};

const clusterDivider = (t: Theme, strong: boolean) =>
  `1px solid ${alpha(
    t.palette.text.primary,
    t.palette.mode === "dark" ? (strong ? 0.42 : 0.2) : strong ? 0.34 : 0.16
  )}`;

// Matches the app's disabled treatment elsewhere (buySellButton, behaviorSwitch):
// dimmed but still legible, since the whole point of the row is to be read.
const SUSPENDED_OPACITY = 0.45;

const gridStyle: SxProps<Theme> = {
  "& .MuiDataGrid-row.contract-suspended": {
    opacity: SUSPENDED_OPACITY,
  },
  "& .MuiDataGrid-cell.cluster-suspended": {
    opacity: SUSPENDED_OPACITY,
  },
  "& .MuiDataGrid-cell.product-suspended": {
    textDecoration: "line-through",
  },
  "&  .MuiDataGrid-row.Mui-selected": {
    backgroundColor: (t) =>
      t.palette.mode === "dark" ? brown[900] : blueGrey[100],
    "& .MuiDataGrid-cell--pinnedLeft": {
      backgroundColor: (t) =>
        t.palette.mode === "dark" ? brown[900] : blueGrey[100],
    },
  },
  "& .MuiDataGrid-cell.selected-cluster-cell": {
    backgroundColor: (t) =>
      t.palette.mode === "dark" ? grey[900] : blueGrey[200],
  },
  "& .MuiDataGrid-cell:focus": {
    outline: 0,
  },
  "& .MuiDataGrid-cell": {
    padding: 0,
    borderRight: (t) => `1px solid ${alpha(t.palette.text.primary, 0.07)}`,
  },
  "& .MuiDataGrid-columnHeader": {
    padding: 0,
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      justifyContent: "center",
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "1.1em",
    fontWeight: 600,
    letterSpacing: "0.01em",
  },
  [`& .MuiDataGrid-cell.${CLUSTER_START_CLASS}`]: {
    borderLeft: (t) => clusterDivider(t, false),
  },
  [`& .MuiDataGrid-columnHeader.${CLUSTER_START_CLASS}`]: {
    borderLeft: (t) => clusterDivider(t, true),
  },
  "& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer":
    {
      borderBottom: (t) => clusterDivider(t, false),
    },
  "& .MuiDataGrid-virtualScrollerRenderZone": {
    "& .MuiDataGrid-row.SingleProduct": {
      "&:nth-of-type(2n)": {
        backgroundColor: (t) =>
          t.palette.mode === "light" ? grey[50] : blueGrey[900],
        "& .MuiDataGrid-cell--pinnedLeft": {
          backgroundColor: (t) =>
            t.palette.mode === "light" ? grey[50] : blueGrey[900],
        },
        "&:hover": {
          backgroundColor: (t) =>
            t.palette.mode === "light" ? grey[100] : blueGrey[800],
          "& .MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: (t) =>
              t.palette.mode === "light" ? grey[100] : blueGrey[800],
          },
        },
      },
    },
  },
  height: "100%",
};
